import React, { useEffect } from 'react';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Button,
  Checkbox,
  Dialog,
  FormField,
  Grid,
  IDialogProps,
  InputGroup,
  majorScale,
  useToast,
} from '../../npaper';
import { useStores } from '../../stores';
import validateEmail from '../../utils/email-validator';

interface FormValues {
  email: string;
  is_admin: boolean;
  is_salesman: boolean;
  is_purchaser: boolean;
  is_warehouse_employee: boolean;
}

export const InviteDialog: React.FC<Omit<IDialogProps, 'title'>> = ({
  isShown,
  ...props
}) => {
  const intl = useIntl();
  const toaster = useToast();
  const { invitationStore } = useStores();

  const [shown, setShown] = useState(isShown);

  const { register, handleSubmit, errors, formState, control } =
    useForm<FormValues>();

  useEffect(() => {
    setShown(isShown);
  }, [isShown]);

  const onSubmit = async (values: FormValues) => {
    // TODO: Create an invitation here
    try {
      await invitationStore.createItem(values);
      setShown(false);
      toaster.addToast({
        message: intl.formatMessage({
          defaultMessage: 'Invite was successfully send',
        }),
        intent: 'success',
      });
    } catch (err) {
      if (err.response && err.response.status === 403) {
        toaster.addToast({
          intent: 'danger',
          message: intl.formatMessage({
            defaultMessage:
              'You do not have permissions to invite users. Only admins can invite new users.',
          }),
        });
      } else {
        toaster.addToast({
          intent: 'danger',
          message: intl.formatMessage(
            {
              defaultMessage:
                'Could not sent invitation to {email}. Please try again later or contact support.',
            },
            {
              email: values.email,
            }
          ),
        });
      }
    }
  };

  return (
    <Dialog
      {...props}
      isShown={shown}
      title={intl.formatMessage({
        defaultMessage: 'Invite user',
        description: 'Dialog title',
      })}
      actions={
        <>
          <Button
            onClick={() => setShown(false)}
            disabled={formState.isSubmitting}
          >
            <FormattedMessage id="defaults.cancel" defaultMessage="Cancel" />
          </Button>
          <Button
            primary
            onClick={handleSubmit(onSubmit)}
            isLoading={formState.isSubmitting}
          >
            <FormattedMessage defaultMessage="Invite user" />
          </Button>
        </>
      }
    >
      <Grid padding={majorScale(2)} paddingTop={majorScale(5)}>
        <FormField
          className="col-span-9"
          inline
          label={intl.formatMessage({
            defaultMessage: 'Email',
          })}
        >
          <InputGroup
            placeholder={intl.formatMessage({
              defaultMessage: 'Email',
            })}
            name="email"
            isInvalid={!!errors.email}
            validationMessage={errors.email && errors.email.message}
            ref={register({
              validate: (v: string) =>
                (!validateEmail(v) &&
                  intl.formatMessage({
                    defaultMessage: 'Please provide a valid email address',
                  })) ||
                true,
              required: intl.formatMessage({
                defaultMessage: 'Please provide provide an email',
                description: 'Validation: Email required',
              }),
            })}
          />
        </FormField>

        <FormField
          className="col-span-9"
          inline
          label={intl.formatMessage({ defaultMessage: 'Roles' })}
        >
          <Controller
            control={control}
            name="is_admin"
            render={({ onChange, value }) => (
              <Checkbox
                onChange={onChange}
                checked={value}
                label={intl.formatMessage({ defaultMessage: 'Admin' })}
              />
            )}
          />
          <Controller
            control={control}
            name="is_salesman"
            defaultValue={true}
            render={({ onChange, value }) => (
              <Checkbox
                onChange={onChange}
                checked={value}
                label={intl.formatMessage({ defaultMessage: 'Sales' })}
              />
            )}
          />
          <Controller
            control={control}
            name="is_purchaser"
            render={({ onChange, value }) => (
              <Checkbox
                onChange={onChange}
                checked={value}
                label={intl.formatMessage({ defaultMessage: 'Purchasing' })}
              />
            )}
          />
          <Controller
            control={control}
            name="is_warehouse_employee"
            render={({ onChange, value }) => (
              <Checkbox
                onChange={onChange}
                checked={value}
                label={intl.formatMessage({ defaultMessage: 'Warehousing' })}
              />
            )}
          />
        </FormField>
      </Grid>
    </Dialog>
  );
};
